import { useCallback, useMemo } from "react";

import {
  faCheck,
  faEye,
  faUserCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { format, parseISO } from "date-fns";
import { useLocation, useNavigate } from "react-router-dom";

import { useReviewCasesAvailableFilters } from "src/api/queries";
import {
  ReviewCase,
  ReviewCaseStatus as ReviewCaseStatusType,
  ReviewCasesAvailableFiltersResponse,
} from "src/api/types";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import { OrganizationUser } from "src/clients/taktile-api";
import { getGroupingIdConfig } from "src/decisionsOverview/groupingIdConfig";
import { Icon } from "src/design-system/Icon";
import { ReviewCaseStatus } from "src/manualReview/ReviewCaseStatus";
import {
  AssigneeFilterDropdown,
  BaseCell,
  FiltersDropdown,
  SortIcon,
  Table,
} from "src/manualReview/Table";
import {
  ReviewCasesOrdering,
  ReviewQueueFilters,
} from "src/manualReview/types";
import { UserPickerCell } from "src/manualReview/UserPicker";
import { convertFiltersToParams } from "src/manualReview/utils";
import { useFlowContext } from "src/router/routerContextHooks";
import { getUrlToReviewCase } from "src/router/urls";
import { dateFromNow } from "src/utils/datetime";

const HeaderCell: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => (
  <div className="flex items-center gap-x-1 py-1.5 pr-4">{children}</div>
);

const columnHelper = createColumnHelper<ReviewCase>();

type GetColumnsArgs = {
  onChangeFilter: (filter: Partial<ReviewQueueFilters>) => void;
  onChangeOrder: (order: ReviewCasesOrdering) => void;
  filters: ReviewQueueFilters;
  availableFilters: ReviewCasesAvailableFiltersResponse | undefined;
  users: OrganizationUser[];
  flowSlug: string;
};

const getColumns = ({
  onChangeFilter,
  onChangeOrder,
  filters,
  availableFilters,
  users,
  flowSlug,
}: GetColumnsArgs) =>
  [
    columnHelper.accessor("number", {
      header: () => <HeaderCell>#</HeaderCell>,
      cell: ({ cell }) => <BaseCell value={String(cell.getValue())} />,
      maxSize: 60,
      minSize: 60,
      size: 60,
    }),
    columnHelper.accessor("decision.start_time", {
      id: "start_time",
      header: () => (
        <HeaderCell>
          Request time
          <SortIcon
            isActive={filters.order === "decision_start_time_desc"}
            onClick={() =>
              onChangeOrder(
                filters.order === "decision_start_time_desc"
                  ? "created_at_asc"
                  : "decision_start_time_desc",
              )
            }
          />
        </HeaderCell>
      ),
      cell: ({ cell }) => (
        <BaseCell
          value={format(parseISO(cell.getValue()), "dd/MM/yyyy HH:mm:ss")}
        />
      ),
      maxSize: 152,
      minSize: 152,
      size: 152,
    }),
    columnHelper.accessor("created_at", {
      id: "time_since_request",
      header: () => (
        <HeaderCell>
          Needs review since
          <SortIcon
            isActive={filters.order === "created_at_asc"}
            onClick={() =>
              onChangeOrder(
                filters.order === "decision_start_time_desc"
                  ? "created_at_asc"
                  : "decision_start_time_desc",
              )
            }
          />
        </HeaderCell>
      ),
      cell: ({ cell }) => (
        <BaseCell value={dateFromNow(cell.getValue(), true)} />
      ),
      maxSize: 152,
      minSize: 152,
      size: 152,
    }),
    columnHelper.accessor("flow.version_name", {
      id: "version",
      header: () => (
        <HeaderCell>
          Version
          <FiltersDropdown
            description="Filter by version:"
            elements={
              availableFilters?.flow_versions.map((version) => ({
                key: version,
                value: version,
              })) ?? []
            }
            isFiltering={!!filters.flowVersionNames}
            selected={filters.flowVersionNames}
            onResetRequest={() =>
              onChangeFilter({ flowVersionNames: undefined })
            }
            onSelect={(keys: string[]) =>
              onChangeFilter({
                flowVersionNames: keys.length === 0 ? undefined : keys,
              })
            }
          />
        </HeaderCell>
      ),
      cell: ({ cell }) => <BaseCell value={cell.getValue()} />,
      maxSize: 120,
      minSize: 80,
      size: 120,
    }),
    columnHelper.accessor("assignee", {
      id: "assignee",
      header: () => (
        <HeaderCell>
          Assigned to
          <AssigneeFilterDropdown
            elements={users
              .filter((user) => availableFilters?.assignees.includes(user.id))
              .map((user) => ({
                key: user.id,
                value: user,
              }))}
            isFiltering={!!filters.assignees}
            selected={filters.assignees}
            onResetRequest={() => onChangeFilter({ assignees: undefined })}
            onSelect={(keys: string[]) =>
              onChangeFilter({
                assignees: keys.length === 0 ? undefined : keys,
              })
            }
          />
        </HeaderCell>
      ),
      cell: ({ cell }) => (
        <UserPickerCell
          caseId={cell.row.original.id}
          etag={cell.row.original.etag}
          flowSlug={flowSlug}
          users={users}
          value={cell.getValue()}
          isInRow
        />
      ),
      maxSize: 170,
      minSize: 100,
      size: 170,
    }),
    columnHelper.accessor("node_name", {
      id: "node_name",
      header: () => (
        <HeaderCell>
          Node
          <FiltersDropdown
            description="Filter by Node name:"
            elements={
              availableFilters?.node_names.map((nodeName) => ({
                key: nodeName,
                value: nodeName,
              })) ?? []
            }
            isFiltering={!!filters.nodeNames}
            selected={filters.nodeNames}
            onResetRequest={() => onChangeFilter({ nodeNames: undefined })}
            onSelect={(keys: string[]) =>
              onChangeFilter({
                nodeNames: keys.length === 0 ? undefined : keys,
              })
            }
          />
        </HeaderCell>
      ),
      cell: ({ cell }) => <BaseCell value={cell.getValue()} />,
      maxSize: 160,
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => (
        <HeaderCell>
          Status
          <FiltersDropdown
            description="Filter by status:"
            elements={[
              {
                key: "needs_review",
                value: "Needs manual review",
              },
              {
                key: "in_progress",
                value: "Review in progress",
              },
            ]}
            isFiltering={!!filters.statuses}
            renderValue={({ value, selected, key }) => (
              <div className="flex w-[240px] items-center justify-between gap-x-1 px-4 py-2.5">
                <Icon
                  variant={key === "in_progress" ? "success" : "active"}
                  icon={faUserCircle}
                  size="xs"
                />
                <span className="inline-block flex-1">{value}</span>
                {selected && (
                  <Icon icon={faCheck} size="2xs" variant="active" />
                )}
              </div>
            )}
            selected={filters.statuses}
            onResetRequest={() => onChangeFilter({ statuses: undefined })}
            onSelect={(keys: string[]) =>
              onChangeFilter({
                statuses:
                  keys.length === 0
                    ? undefined
                    : (keys as unknown as Exclude<
                        ReviewCaseStatusType,
                        "completed"
                      >[]),
              })
            }
          />
        </HeaderCell>
      ),
      cell: ({ cell }) => <ReviewCaseStatus status={cell.getValue()} />,
      maxSize: 180,
      minSize: 180,
      size: 180,
    }),
    columnHelper.accessor("decision.id", {
      id: "id",
      header: () => <HeaderCell>Decision ID</HeaderCell>,
      cell: ({ cell }) => (
        <TruncatedIdPill
          dataLoc="manual-review-copy-decision-id"
          id={cell.getValue()}
        />
      ),
      maxSize: 120,
      minSize: 100,
      size: 120,
    }),
    columnHelper.accessor("decision.entity_id", {
      id: "entity_id",
      header: () => <HeaderCell>{getGroupingIdConfig().label}</HeaderCell>,
      cell: ({ cell }) => {
        const groupingId = cell.getValue();
        return groupingId ? (
          <TruncatedIdPill id={groupingId} />
        ) : (
          <BaseCell value="-" />
        );
      },
      maxSize: 120,
      minSize: 100,
      size: 120,
    }),
    columnHelper.display({
      id: "actions",
      header: () => null,
      cell: () => (
        <span
          className="invisible text-right text-gray-500 group-hover/row:visible"
          data-loc="manual-review-open-case"
        >
          <Icon icon={faEye} size="xs" />
        </span>
      ),
      maxSize: 30,
      minSize: 30,
      size: 30,
    }),
  ] as const as ColumnDef<ReviewCase>[];

type ReviewQueueTableProps = {
  cases: ReviewCase[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  canFetchNextPage: boolean;
  hidden: boolean;
  onChangeOrder: (order: ReviewCasesOrdering) => void;
  onChangeFilter: (filter: Partial<ReviewQueueFilters>) => void;
  filters: ReviewQueueFilters;
  users: OrganizationUser[] | undefined;
};

export const ReviewQueueTable: React.FC<ReviewQueueTableProps> = ({
  cases,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
  canFetchNextPage,
  hidden,
  onChangeOrder,
  onChangeFilter,
  filters,
  users = [],
}) => {
  const { workspace, flow, orgId } = useFlowContext();

  const availableFilters = useReviewCasesAvailableFilters(
    workspace.base_url,
    flow.slug,
    convertFiltersToParams(filters),
  );
  const navigate = useNavigate();
  const { search } = useLocation();

  // We need to memoize columns because
  // tanstack remounts the table on every render
  const columns = useMemo(() => {
    return getColumns({
      onChangeOrder,
      onChangeFilter,
      filters,
      availableFilters: availableFilters.data,
      users,
      flowSlug: flow.slug,
    });
  }, [
    availableFilters.data,
    filters,
    flow.slug,
    onChangeFilter,
    onChangeOrder,
    users,
  ]);

  const rowOnClick = useCallback(
    (row: Row<ReviewCase>, e: React.MouseEvent) => {
      const url = getUrlToReviewCase(
        orgId,
        workspace.id,
        flow.id,
        row.original.id,
        search,
      );
      if (e.metaKey || e.ctrlKey) {
        window.open(`/decide${url}`, "_blank");
      } else {
        navigate(url);
      }
    },
    [flow.id, navigate, orgId, search, workspace.id],
  );

  return (
    !hidden && (
      <Table
        canFetchNextPage={canFetchNextPage}
        columns={columns}
        data={cases}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoading={isLoading}
        rowOnClick={rowOnClick}
      />
    )
  );
};
